import { Component, EventEmitter, inject, Input, model, OnInit, Output, signal } from '@angular/core';
import { ActionSheetController, ToastController } from '@ionic/angular';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Image } from '../../../models/image.model';
import { Picture } from '../../../models/picture.interface.cs';
import { PhotoService } from '../../../services/photo.service';
import { ShowService } from '../../show.service';

/**
 * Photo picker shared by logbook, task form, and inspection forms.
 * Uploads on pick; exposes two model() signals (`imageIds`,
 * `removedImageIds`) that the parent binds two-way to fields on its
 * entity. No event handlers needed in the parent.
 *
 * Example:
 *   <app-picture-picker
 *       [(imageIds)]="task.imageIds"
 *       [(removedImageIds)]="task.removedImageIds"
 *       [existingImages]="task.images" />
 *
 * Display rules:
 * - `[existingImages]` are rendered with delete buttons; deletes are
 *   tracked in `removedImageIds` so the parent can pass them to the BE
 *   on save.
 * - Newly-picked images are uploaded immediately to /uploadFile, the
 *   returned id is appended to the local `newImages` list and written
 *   to the `imageIds` model signal.
 * - Both lists render via `${environment.fileUrl}/${fileName}` — the
 *   files are on disk the moment they're emitted.
 *
 * Known limitations (carried over from tasks):
 * - If the user uploads photos and then cancels the save, the files
 *   stay on disk as orphans. Sweep is a phase-2 concern.
 */
@Component({
    selector: 'app-picture-picker',
    templateUrl: './picture-picker.component.html',
    styleUrls: ['./picture-picker.component.scss'],
    standalone: false
})
export class PicturePickerComponent implements OnInit {
    /** Server-side images already attached to the log (edit mode). */
    @Input() public existingImages: Image[] = [];

    @Input() public maxNumberImages = 64;

    /**
     * @deprecated Old base64 emission — never populated by the new
     * upload-on-pick flow. Retained on the API so callers that still
     * bind `[selectedImages]` don't crash; phase 2 deletes it.
     */
    @Input() public selectedImages: Picture[] = [];

    /**
     * @deprecated Replaced by `imageIds` + `removedImageIds` model signals.
     * Kept so phase-1-deleted callers still type-check; not emitted.
     */
    @Output() public selectedImagesEmitter = new EventEmitter<Picture[]>();

    /**
     * Two-way signal binding for the cumulative list of newly-uploaded
     * ImageFile ids this session. Parents use [(imageIds)]="entity.imageIds"
     * and the picker writes back via .set() — no event handlers needed.
     */
    public imageIds = model<number[]>([]);

    /**
     * Two-way signal binding for the cumulative list of existing image
     * ids the user removed during this session.
     */
    public removedImageIds = model<number[]>([]);

    public newImages: Image[] = [];
    public removedExistingImageIds: number[] = [];
    /**
     * Signal (not a plain field) so the uploading spinner re-renders even
     * when the value is toggled from a Capacitor callback that resolves
     * outside Angular's zone, under an OnPush parent (e.g. the Performer Fly
     * inspection form). A plain boolean left the spinner invisible on device.
     */
    public readonly isUploading = signal(false);

    private readonly fileUrlBase = environment.fileUrl;

    private actionSheetController: ActionSheetController = inject(ActionSheetController);
    private photoService: PhotoService = inject(PhotoService);
    private toastController: ToastController = inject(ToastController);
    private http: HttpClient = inject(HttpClient);
    private showService: ShowService = inject(ShowService);

    public ngOnInit(): void {}

    public getImageUrl(image: Image | Picture): string {
        return `${this.fileUrlBase}/${image.fileName}`;
    }

    public visibleExistingImages(): Image[] {
        return (this.existingImages ?? []).filter(img => !this.removedExistingImageIds.includes(img.id));
    }

    public totalImageCount(): number {
        return this.visibleExistingImages().length + this.newImages.length;
    }

    public isAtMaxImages(): boolean {
        return this.totalImageCount() >= this.maxNumberImages;
    }

    public async selectImagePicker(): Promise<void> {
        if (this.isUploading()) {
            return;
        }
        if (this.isAtMaxImages()) {
            await this.showToast(`Maximaal ${this.maxNumberImages} afbeeldingen toegestaan`, 'warning');
            return;
        }
        await this.photoService.openPhotoSelector(
            async (fileName, base64) => this.uploadPickedPhoto(fileName, base64),
            async error => this.displayErrorToast(error),
        );
    }

    public async showDeleteImageActionSheet(image: Image, isExisting: boolean): Promise<void> {
        const actionSheet = await this.actionSheetController.create({
            header: 'Weet je zeker dat je deze afbeelding wilt verwijderen?',
            buttons: [
                {
                    text: 'Verwijderen',
                    role: 'destructive',
                    handler: () => {
                        if (isExisting) {
                            this.removeExistingImage(image);
                        } else {
                            this.removeNewlyUploadedImage(image);
                        }
                    },
                },
                {
                    text: 'Annuleren',
                    role: 'cancel',
                },
            ],
        });
        await actionSheet.present();
    }

    private async uploadPickedPhoto(fileName: string, base64: string): Promise<void> {
        this.isUploading.set(true);
        try {
            const blob = this.base64ToBlob(base64, this.guessMimeType(fileName));
            const formData = new FormData();
            formData.append('file', blob, fileName || 'photo.jpg');
            const showId = await this.showService.getActiveShowId();
            formData.append('showId', String(showId ?? 1));

            // Auth headers come from AuthInterceptor — see P0.6 note in api.service.ts.
            const response: any = await this.http
                .post(`${environment.apiUrl}/uploadFile`, formData)
                .toPromise();

            const newId: number = response?.imageFile?.id;
            const newFileName: string = response?.imageFile?.fileName ?? fileName;

            if (!newId) {
                throw new Error('Upload response missing imageFile.id');
            }

            const uploaded: Image = Image.createFromJson({
                id: newId,
                fileName: newFileName,
                idParent: response?.imageFile?.idParent,
                status: response?.imageFile?.status,
            });
            this.newImages.push(uploaded);
            this.emitNewIds();
        } catch (error) {
            console.error('[PicturePickerComponent] uploadPickedPhoto() error:', error);
            await this.showToast('Afbeelding uploaden mislukt', 'danger');
        } finally {
            this.isUploading.set(false);
        }
    }

    private removeNewlyUploadedImage(image: Image): void {
        this.newImages = this.newImages.filter(i => i.id !== image.id);
        this.emitNewIds();
    }

    private removeExistingImage(image: Image): void {
        this.removedExistingImageIds = [...this.removedExistingImageIds, image.id];
        this.removedImageIds.set([...this.removedExistingImageIds]);
    }

    private emitNewIds(): void {
        this.imageIds.set(this.newImages.map(i => i.id));
    }

    private base64ToBlob(base64: string, mimeType: string): Blob {
        const cleaned = base64.includes(',') ? base64.split(',')[1] : base64;
        const byteCharacters = atob(cleaned);
        const byteNumbers = new Array<number>(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: mimeType });
    }

    private guessMimeType(fileName: string): string {
        if (!fileName) { return 'image/jpeg'; }
        const lower = fileName.toLowerCase();
        if (lower.endsWith('.png')) { return 'image/png'; }
        if (lower.endsWith('.gif')) { return 'image/gif'; }
        if (lower.endsWith('.webp')) { return 'image/webp'; }
        return 'image/jpeg';
    }

    private async displayErrorToast(error: unknown): Promise<void> {
        console.warn('PicturePickerComponent: error while picking picture:', error);
        await this.showToast('Er is een fout opgetreden tijdens het inladen van de foto.', 'danger');
    }

    private async showToast(message: string, color: 'success' | 'danger' | 'warning'): Promise<void> {
        const toast = await this.toastController.create({ message, duration: 2000, color });
        await toast.present();
    }
}
